<template>
    <div
        :id="data.id"
        class="border-t border-l-4 border-dividers py-6 px-6 flex hash-links-header-padding"
        :class="{ 'is-track-colored': trackColor != 'transparent' }"
        :style="{
            backgroundColor: transparentTrackColor,
            borderLeftColor: trackColor
        }"
    >
        <div class="flex-grow">
            <p class="m-0 text-md mb-1">
                {{ data.formattedStart }} - {{ data.formattedEnd }}
            </p>
            <p
                v-if="data.track"
                class="m-0 font-bold text-lg"
                :style="{ color: trackColor }"
            >
                {{ this.data.track.title }}
            </p>
            <a class="text-dark" v-if="data.schedulePageUrl" :href="titleWithScheduleUrl">
                <p class="font-bold text-lg m-0 my-1">{{ data.title }}</p>
            </a>
            <p v-else class="font-bold text-lg m-0 my-1">{{ data.title }}</p>
            <p class="italic m-0">{{ data.location }}</p>
            <div
                v-if="hasInnerContent && isExpanded"
                class="mt-4 content-container"
                :class="{ 'mb-4': data.sponsors.length }"
            >
                <div
                    v-if="data.content"
                    v-html="data.content"
                    class="remove-last-margin"
                ></div>
                <p v-if="data.speakers.length" class="font-bold text-md">
                    Speaker{{ data.speakers.length > 1 ? "s" : "" }}
                </p>
                <div
                    v-if="data.speakers.length"
                    class="agenda-flex-item-container"
                    :class="{ 'mt-2': data.content }"
                >
                    <div
                        v-for="speaker in data.speakers"
                        :key="speaker.title"
                        class="agenda-flex-item agenda-flex-item--third flex flex-row md:flex-col xxl:flex-row"
                    >
                        <img
                            :src="speaker.imageUrl"
                            :alt="`Photo of ${speaker.title}`"
                            class="block mr-2 w-24 h-24 flex-shrink-0"
                        />
                        <div>
                            <p class="mb-1 text-midwinter-blue">
                                <a
                                    :href="speakerBioUrl(speaker)"
                                    class="font-semibold text-midwinter-blue"
                                    >{{ speaker.title }}</a
                                >
                            </p>
                            <p v-if="speaker.jobTitle" class="my-1 text-sm">
                                {{ speaker.jobTitle }}
                            </p>
                            <p
                                v-if="speaker.company"
                                class="mt-1 mb-0 text-sm font-semibold"
                            >
                                {{ speaker.company }}
                            </p>
                        </div>
                    </div>
                </div>
                <p
                    v-if="data.moderators && data.moderators.length"
                    class="font-bold text-md"
                >
                    Moderator{{ data.moderators.length > 1 ? "s" : "" }}
                </p>
                <div
                    v-if="data.moderators && data.moderators.length"
                    class="agenda-flex-item-container"
                    :class="{ 'mt-2': data.content }"
                >
                    <div
                        v-for="speaker in data.moderators"
                        :key="speaker.title"
                        class="agenda-flex-item agenda-flex-item--third flex flex-row md:flex-col xxl:flex-row"
                    >
                        <img
                            :src="speaker.imageUrl"
                            :alt="`Photo of ${speaker.title}`"
                            class="block mr-2 w-24 h-24 flex-shrink-0"
                        />
                        <div>
                            <p class="mb-1">
                                <a
                                    :href="speakerBioUrl(speaker)"
                                    class="font-semibold"
                                    >{{ speaker.title }}</a
                                >
                            </p>
                            <p v-if="speaker.jobTitle" class="my-1 text-sm">
                                {{ speaker.jobTitle }}
                            </p>
                            <p
                                v-if="speaker.company"
                                class="mt-1 mb-0 text-sm font-semibold"
                            >
                                {{ speaker.company }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="data.sponsors.length">
                    <p class="m-0 text-md mb-4 color-fmi-grey">Sponsored by {{ allSponsorNames }}:</p>
                    <div class="flex flex-wrap">
                        <div class="sponsor mr-4 mb-4" v-for="sponsor in data.sponsors" :key="sponsor.title">
                            <a
                                v-if="sponsor.url"
                                :href="getSponsorUrl(sponsor.url)"
                                class="border p-1 mr-1 mb-1 w-24 h-16 block border-dividers border-solid"
                            >
                                <img
                                    :src="sponsor.imageUrl"
                                    :alt="`${sponsor.title} Logo`"
                                />
                            </a>
                            <div v-else class="border p-2 mr-1 mb-1 w-24 h-16">
                                <img
                                    :src="sponsor.imageUrl"
                                    :alt="`${sponsor.title} Logo`"
                                    class="block"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="ml-2 flex-shrink-0">
            <a
                v-if="hasInnerContent"
                @click.prevent="toggleExpand()"
                href="#"
                class="circle-toggler"
                :class="{ active: isExpanded }"
            >
                <p
                    v-if="hasInnerContent"
                    class="text-md mb-0 text-midwinter-blue no-print"
                    style="width: 110px"
                >
                    View {{ isExpanded ? "Less" : "More" }}
                </p>
                <div class="circle no-print">
                    <div class="horizontal"></div>
                    <div class="vertical"></div>
                </div>
            </a>
        </div>
    </div>
</template>

<script>

export default {
    props: {
        data: { type: Object },
        isExpanded: { type: Boolean }
    },
    data() {
        return {
            isExpanded: this.isExpanded
        };
    },
    computed: {
        titleWithScheduleUrl() {
            return this.data.schedulePageUrl + "#" + this.data.id;
        },
        hasInnerContent() {
            return this.data.content || this.data.speakers.length;
        },
        trackColor() {
            return this.data.track && this.data.track.color
                ? this.data.track.color
                : "transparent";
        },
        transparentTrackColor() {
            return this.trackColor && this.trackColor != "transparent"
                ? `${this.trackColor}1a`
                : "transparent";
        },
        allSponsorNames() {
            const names = this.data.sponsors.map((sponsor) => sponsor.title);

            if (names.length == 1) {
                return names[0];
            }

            if (names.length == 2) {
                return names.join(" and ");
            }

            if (names.length > 2) {
                names[names.length - 1] = `and ${names[names.length - 1]}`;
                return names.join(", ");
            }

            return "";
        }
    },
    methods: {
        getSponsorUrl(url) {
            if(!url.includes('http://') && !url.includes('https://')) return 'http://' + url;
            return url;
        },
        speakerBioUrl(speaker) {
            let toReturn = speaker.url ? speaker.url : speaker.defaultUrl;
            if(toReturn.indexOf("/agenda/speakers") == -1) {
                toReturn = window.location.href + "/agenda/speakers" + toReturn;
            }
            return toReturn;
        },
        toggleExpand() {
            this.isExpanded = !this.isExpanded;
        }
    }
};
</script>

<style lang="scss" scoped>
    .content-container{
        color: #000;
    }
</style>


// WEBPACK FOOTER //
// src/js/vue-components/conference-agenda/ConfAgendaSession.vue